import storage from '../config/appStorage';
import {
  ACTIVE_READING_PLAN,
  AUTH_PROMPT_DISMISSED,
  BOOKMARK_CHAPTER,
  DEFAULT_LAST_READING_CHAPTER,
  DEFAULT_THEME_SETTINGS,
  LAST_LISTENING,
  LAST_LISTENING_POSITION,
  LAST_READING_CHAPTER,
  THEME_SETTINGS,
  VERSE_OF_DAY_ENABLED,
  VERSE_OF_DAY_HOUR,
  VERSE_OF_DAY_MINUTE,
  PENDING_VERSE_OF_DAY_NAV,
  track,
} from '../constants/stringsAndFields';

let themePersistHook = null;
let themeRefreshFromStorage = null;

/** Optional hook for syncing theme/settings to SQLite + Supabase after storage write. */
export const registerThemePersistHook = fn => {
  themePersistHook = fn;
};

/** ThemeProvider registers this so we can re-read storage after remote settings are applied. */
export const registerThemeRefreshFromStorage = fn => {
  themeRefreshFromStorage = fn;
};

export const refreshThemeFromStorage = async () => {
  await themeRefreshFromStorage?.();
};

/** Replace THEME_SETTINGS without running the persist hook (e.g. after pulling remote settings). */
export const replaceThemeSettingsSilent = async fullSettings => {
  try {
    await storage.setItem(THEME_SETTINGS, JSON.stringify(fullSettings));
  } catch (e) {
    console.log(e);
  }
};

export const getLastReading = async () => {
  try {
    const data = await storage.getItem(LAST_READING_CHAPTER);
    return data ? data : DEFAULT_LAST_READING_CHAPTER;
  } catch (e) {
    console.log(e);
  }
  return DEFAULT_LAST_READING_CHAPTER;
};

export const saveLastReading = async chapterId => {
  try {
    await storage.setItem(LAST_READING_CHAPTER, chapterId);
  } catch (e) {
    console.log(e);
  }
};

export const getLastListening = async () => {
  try {
    const data = await storage.getItem(LAST_LISTENING);
    if (data) return JSON.parse(data);
  } catch (e) {
    console.log(e);
  }
  return null;
};

export const saveLastListening = async (activeTrack = null, position = '0') => {
  try {
    const defaultListening = {
      [track]: '',
      [LAST_LISTENING_POSITION]: '0',
    };
    const existingListening = await getLastListening();
    if (existingListening) {
      if (activeTrack) existingListening[track] = activeTrack;
      if (position)
        existingListening[LAST_LISTENING_POSITION] = String(position);
    } else {
      if (activeTrack) defaultListening[track] = activeTrack;
      if (position)
        defaultListening[LAST_LISTENING_POSITION] = String(position);
    }
    await storage.setItem(
      LAST_LISTENING,
      JSON.stringify(existingListening ? existingListening : defaultListening),
    );
  } catch (e) {
    console.log(e);
  }
};

export const deleteLastListening = async () => {
  await storage.removeItem(LAST_LISTENING);
};

export const getBookmarkReading = async () => {
  try {
    const data = await storage.getItem(BOOKMARK_CHAPTER);
    return data ? data : null;
  } catch (e) {
    console.log(e);
  }
  return null;
};

export const saveBookmarkReading = async chapterId => {
  try {
    await storage.setItem(BOOKMARK_CHAPTER, chapterId);
  } catch (e) {
    console.log(e);
  }
};

export const getActiveReadingPlan = async () => {
  try {
    const data = await storage.getItem(ACTIVE_READING_PLAN);
    return data ? data : null;
  } catch (e) {
    console.log(e);
  }
  return null;
};

export const setActiveReadingPlan = async readingPlanId => {
  try {
    await storage.setItem(ACTIVE_READING_PLAN, readingPlanId);
  } catch (e) {
    console.log(e);
  }
};

export const resetBookmarkReading = async _ => {
  try {
    await storage.removeItem(BOOKMARK_CHAPTER);
  } catch (e) {
    console.log(e);
  }
};

export const saveThemeSettings = async (
  updatedSettingKey,
  updatedSettingValue,
) => {
  try {
    const data = await storage.getItem(THEME_SETTINGS);
    const storedSettings = data ? JSON.parse(data) : DEFAULT_THEME_SETTINGS;
    storedSettings[updatedSettingKey] = updatedSettingValue;
    await storage.setItem(THEME_SETTINGS, JSON.stringify(storedSettings));
    try {
      await themePersistHook?.();
    } catch (err) {
      console.log(err);
    }
  } catch (e) {
    console.log(e);
  }
};

export const getThemeSettings = async () => {
  try {
    const data = await storage.getItem(THEME_SETTINGS);
    return data ? JSON.parse(data) : DEFAULT_THEME_SETTINGS;
  } catch (e) {
    console.log(e);
  }
  return DEFAULT_THEME_SETTINGS;
};

export const getAuthPromptDismissed = async () => {
  try {
    const v = await storage.getItem(AUTH_PROMPT_DISMISSED);
    return v === '1' || v === 'true';
  } catch (e) {
    console.log(e);
  }
  return false;
};

export const setAuthPromptDismissed = async () => {
  try {
    await storage.setItem(AUTH_PROMPT_DISMISSED, '1');
  } catch (e) {
    console.log(e);
  }
};

const defaultVerseOfDay = () => ({
  [VERSE_OF_DAY_ENABLED]: false,
  [VERSE_OF_DAY_HOUR]: 8,
  [VERSE_OF_DAY_MINUTE]: 0,
});

export const getVerseOfDaySettings = async () => {
  try {
    const rows = await storage.multiGet([
      VERSE_OF_DAY_ENABLED,
      VERSE_OF_DAY_HOUR,
      VERSE_OF_DAY_MINUTE,
    ]);
    const enabledRaw = rows[0][1];
    const hourRaw = rows[1][1];
    const minuteRaw = rows[2][1];
    if (
      enabledRaw === null &&
      hourRaw === null &&
      minuteRaw === null
    ) {
      return defaultVerseOfDay();
    }
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    return {
      [VERSE_OF_DAY_ENABLED]: enabledRaw === '1' || enabledRaw === 'true',
      [VERSE_OF_DAY_HOUR]: Number.isFinite(hour) ? hour : 8,
      [VERSE_OF_DAY_MINUTE]: Number.isFinite(minute) ? minute : 0,
    };
  } catch (e) {
    console.log(e);
  }
  return defaultVerseOfDay();
};

export const saveVerseOfDaySettings = async partial => {
  try {
    if (Object.prototype.hasOwnProperty.call(partial, VERSE_OF_DAY_ENABLED)) {
      await storage.setItem(
        VERSE_OF_DAY_ENABLED,
        partial[VERSE_OF_DAY_ENABLED] ? '1' : '0',
      );
    }
    if (Object.prototype.hasOwnProperty.call(partial, VERSE_OF_DAY_HOUR)) {
      await storage.setItem(
        VERSE_OF_DAY_HOUR,
        String(Math.max(0, Math.min(23, partial[VERSE_OF_DAY_HOUR]))),
      );
    }
    if (Object.prototype.hasOwnProperty.call(partial, VERSE_OF_DAY_MINUTE)) {
      await storage.setItem(
        VERSE_OF_DAY_MINUTE,
        String(Math.max(0, Math.min(59, partial[VERSE_OF_DAY_MINUTE]))),
      );
    }
  } catch (e) {
    console.log(e);
  }
};

export const setPendingVerseOfDayNav = async verseId => {
  try {
    if (verseId == null || verseId === '') {
      return;
    }
    await storage.setItem(PENDING_VERSE_OF_DAY_NAV, String(verseId));
  } catch (e) {
    console.log(e);
  }
};

/** Returns the stored verse id and clears the key (single consume). */
export const consumePendingVerseOfDayNav = async () => {
  try {
    const raw = await storage.getItem(PENDING_VERSE_OF_DAY_NAV);
    if (raw == null || raw === '') {
      return null;
    }
    await storage.removeItem(PENDING_VERSE_OF_DAY_NAV);
    return raw;
  } catch (e) {
    console.log(e);
  }
  return null;
};
